// eslint-disable-next-line no-unused-vars
import React, { memo, useEffect, useState, useMemo } from 'react';
import { stringify } from 'query-string';
import { useTranslation } from 'react-i18next';
import omit from 'lodash/omit';

import useRouter from 'Hooks/useRouter';
import useFetch from 'Hooks/useFetch';
import useUser from 'Hooks/useUser';
import useAsyncError from 'Hooks/useAsyncError';
import useAnalytics from 'Hooks/useAnalytics';
import BalanceComponent from 'Components/Reports/Balance';
import { API_BALANCE } from 'Constants';

const defaultSortId = 'document_date-desc';

const Balance = memo(() => {
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = useState(null);
  const fetcher = useFetch();
  const { t } = useTranslation();
  const throwError = useAsyncError();
  const { data: { email, loggedIn } = {} } = useUser();
  const { query, location, push } = useRouter();
  const analytics = useAnalytics();

  const sortOptions = useMemo(() => [
    { id: 'number-asc', label: `${ t('pages.reference')} ${ t('orders.ascending')}` },
    { id: 'number-desc', label: `${ t('pages.reference')} ${ t('orders.descending')}` },
    { id: 'document_date-asc', label: `${ t('pages.date')} ${ t('orders.ascending')}` },
    { id: 'document_date-desc', label: `${ t('pages.date')} ${ t('orders.descending')}` },
    { id: 'due_date-asc', label: `${ t('pages.dueDate')} ${ t('orders.ascending')}` },
    { id: 'due_date-desc', label: `${ t('pages.dueDate')} ${ t('orders.descending')}` },
    { id: 'value-asc', label: `${ t('pages.netValue')} ${ t('orders.ascending')}` },
    { id: 'value-desc', label: `${ t('pages.netValue')} ${ t('orders.descending')}` },
  ], []);

  const defaultSort = sortOptions[3];
  const currentSortId = query?.order_by
    ? `${ query.order_by }-${ query.order_direction }`
    : defaultSortId;
  const [currentSort, setSort] = useState(
    currentSortId !== defaultSort.id
      ? sortOptions.find(item => item.id === currentSortId)
      : defaultSort
  );

  const getInvoices = () => {
    setLoading(true);

    let path = API_BALANCE;

    if (query) {
      path += `?${ stringify({ ...query }) }`;
    }

    fetcher(path).then(res => {
      if (res.length === 0) {
        setInvoices([]);
        setPagination(null);
        return;
      }

      const resPagination = {
        currentPage: res?.currentPage,
        recordsPerPage: res?.recordsPerPage,
        totalPages: res?.totalPages,
        totalRecords: res?.totalRecords
      };

      const mappedInvoices = res.invoices.map(invoice => ({
        ...invoice,
        id: invoice?.number,
        reference: /^\d+$/.test(invoice?.reference) ? invoice?.reference : null,
        date: invoice?.date,
        dueDate: invoice?.dueDate,
        netValue: {
          value: invoice?.netValue,
          currency: invoice?.currency,
        }
      }));

      setInvoices(mappedInvoices);
      setPagination(resPagination);
    }).catch(err => throwError(err))
      .finally(() => setLoading(false));
  };

  // Get Invoices on page load
  useEffect(() => {
    if (loggedIn) {
      getInvoices();
    }

    // if (Object.keys(query).length && !loggedIn) {
    //   push({
    //     location: location.pathname,
    //     search: ''
    //   });
    // }
  }, [location.pathname, query, email, loggedIn]);

  const onMobileOrderChange = sort => {
    const [orderBy, orderDirection] = sort.split('-');
    analytics.track('sort_reports', { order_by: orderBy, order_direction: orderDirection });

    if (orderDirection === 'none' || sort === defaultSortId) {
      setSort(sortOptions.find(item => item.id === defaultSortId));
      push({
        location: location.pathname,
        search: stringify(omit(query || {}, ['order_by', 'order_direction']))
      });
      return;
    }

    setSort(sortOptions.find(item => item.id === sort));

    const queryParams = {
      ...(omit(query || {}, ['order_by', 'order_direction', 'page'])),
      order_by: orderBy,
      order_direction: orderDirection
    };

    push({
      location: location.pathname,
      search: stringify(queryParams)
    });
  };

  return (
    <BalanceComponent
      invoices={ invoices }
      loading={ loading }
      pagination={ pagination }
      handleMobileOrderChange={ onMobileOrderChange }
      currentSort={ currentSort }
      defaultSortId={ defaultSortId }
      sortOptions={ sortOptions }
      getInvoices={ getInvoices }
    />
  );
});

Balance.displayName = 'BalanceContainer';

export default Balance;
